/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { decorate, computed, runInAction, observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Icon } from 'semantic-ui-react';
import { isStoreLoading, isStoreError, isStoreEmpty } from '@amzn/base-ui/dist/models/BaseStore';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';

import UserOnboarding from '../users/UserOnboarding';
import SelectComputePlatformStep from './SelectComputePlatformStep';
import ConfigureComputePlatformStep from './ConfigureComputePlatformStep';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - onPrevious (via props)
// - onCompleted (via props) a function is called after a call to create an environment is performed
// - studyIds (via props) (optional) an array of the selected study ids
// - currentStep (via props) an instance of the CurrentStep model
// - computePlatformsStore (via injection)
// - clientInformationStore (via injection)
class ComputePlatformSetup extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.selectedPlatformId = undefined;
      this.onboardingOpen = false;
    });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    swallowError(this.computePlatformsStore.load());
  }

  get userStore() {
    return this.props.userStore;
  }

  get computePlatformsStore() {
    return this.props.computePlatformsStore;
  }

  get clientInformationStore() {
    return this.props.clientInformationStore;
  }

  get currentStep() {
    return this.props.currentStep;
  }

  setOnboarding = value => {
    this.onboardingOpen = value;
  };

  handleConfigureCredentials = event => {
    event.preventDefault();
    event.stopPropagation();
    this.setOnboarding(true);
  };

  handleSelectComputePlatform = async platformId => {
    this.selectedPlatformId = platformId;
    const platformsStore = this.computePlatformsStore;
    if (!platformsStore) return;

    const platformStore = platformsStore.getComputePlatformStore(platformId);
    await platformStore.load();

    const clientInformationStore = this.clientInformationStore;
    try {
      await clientInformationStore.load();
    } catch (error) {
      // ignore intentionally
    }

    window.scrollTo(0, 0);
    runInAction(() => {
      this.currentStep.setStep('selectComputeConfiguration');
    });
  };

  handlePrevious = () => {
    const currentStep = this.currentStep;
    if (currentStep.step === 'selectComputePlatform') {
      this.props.onPrevious();
      return;
    }

    this.currentStep.setStep('selectComputePlatform');
  };

  handleCompleted = async environment => {
    return this.props.onCompleted(environment);
  };

  get studyIds() {
    return this.props.studyIds;
  }

  get rootVolume() {
    return this.props.rootVolume;
  }

  get hyperthreading() {
    return this.props.enableHyperthreading;
  }

  render() {
    const store = this.computePlatformsStore;
    let content = null;

    if (isStoreError(store)) {
      content = this.renderLoadingError();
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder className="mt2" />;
    } else if (isStoreEmpty(store)) {
      content = this.renderEmpty();
    } else {
      content = this.renderContent();
    }

    return (
      <>
        {content}
        {this.onboardingOpen && <UserOnboarding onclose={() => this.setOnboarding(false)} />}
      </>
    );
  }

  renderContent() {
    const step = this.currentStep.step;
    const platformId = this.selectedPlatformId;
    const studyIds = this.studyIds;
    const rootVolume = this.rootVolume;
    const enableHyperthreading = this.hyperthreading;
    const user = this.userStore.user;
    const hasProjects = user.hasProjects;
    const isExternalResearcher = user.isExternalResearcher;
    const canCreateWorkspace = user.capabilities.canCreateWorkspace;
    const hasCredentials = user.hasCredentials;
    let content = null;

    if (!canCreateWorkspace) {
      return this.renderEmpty();
    }

    if (!isExternalResearcher && !hasProjects) {
      return this.renderMissingProjects();
    }

    if (isExternalResearcher && !hasCredentials) {
      return this.renderMissingCredentials();
    }

    if (step === 'selectComputePlatform') {
      content = (
        <SelectComputePlatformStep onPrevious={this.handlePrevious} onNext={this.handleSelectComputePlatform} />
      );
    } else if (step === 'selectComputeConfiguration') {
      content = (
        <ConfigureComputePlatformStep
          platformId={platformId}
          studyIds={studyIds}
          rootVolume={rootVolume}
          enableHyperthreading={enableHyperthreading}
          onPrevious={this.handlePrevious}
          onCompleted={this.handleCompleted}
        />
      );
    }

    return content;
  }

  renderLoadingError() {
    const store = this.computePlatformsStore;
    return (
      <>
        <ErrorBox error={store.error} className="p0 mt2 mb3" />
        {this.renderButtons()}
      </>
    );
  }

  renderEmpty() {
    return (
      <>
        <div className="study-empty-state mt2">
          <Icon name="server" />
          <div className="study-empty-state-title">No compute platform</div>
          <div className="study-empty-state-text">
            There are no compute platforms to choose from. Your role might be restricted. Please contact your administrator.
          </div>
        </div>
        {this.renderButtons()}
      </>
    );
  }

  renderMissingProjects() {
    return (
      <>
        <div className="study-empty-state mt2">
          <Icon name="lock" />
          <div className="study-empty-state-title">Missing association with projects</div>
          <div className="study-empty-state-text">
            You currently do not have an active project. Please contact RACE.
          </div>
        </div>
        {this.renderButtons()}
      </>
    );
  }

  renderMissingCredentials() {
    return (
      <>
        <div className="study-empty-state mt2">
          <Icon name="key" />
          <div className="study-empty-state-title">No AWS credentials</div>
          <div className="study-empty-state-text">To manage workspaces, click Configure AWS Credentials.</div>
          <button
            className="workspace-action-btn workspace-action-btn-secondary"
            onClick={this.handleConfigureCredentials}
            style={{ marginTop: '12px' }}
          >
            Configure AWS Credentials
          </button>
        </div>
        {this.renderButtons()}
      </>
    );
  }

  renderButtons() {
    return (
      <div className="mt3" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button className="workspace-action-btn workspace-action-btn-secondary" onClick={this.handlePrevious}>
          <Icon name="left arrow" style={iconStyle} /> Previous
        </button>
        <button className="workspace-action-btn workspace-action-btn-primary" disabled>
          Next <Icon name="right arrow" style={iconStyle} />
        </button>
      </div>
    );
  }
}

decorate(ComputePlatformSetup, {
  handleSelectComputePlatform: action,
  handlePrevious: action,
  handleCompleted: action,
  setOnboarding: action,
  studyIds: computed,
  userStore: computed,
  computePlatformsStore: computed,
  clientInformationStore: computed,
  currentStep: computed,
  selectedPlatformId: observable,
  onboardingOpen: observable,
});

export default inject('userStore', 'computePlatformsStore', 'clientInformationStore')(observer(ComputePlatformSetup));
